import { useRef, useEffect } from 'react';
import QRCodeStyling from 'qr-code-styling';
import './QRCode.css';

/**
 * QR Code компонент на базе qr-code-styling.
 *
 * @param {string} url - URL для кодирования
 * @param {number} [size=160] - Размер QR-кода в пикселях
 * @param {string} [fgColor='#000000'] - Цвет точек QR
 * @param {string} [bgColor='#FFFFFF'] - Цвет фона QR
 * @param {string} [logoUrl] - URL внешнего логотипа для центра QR (HTTPS на тот
 *   же origin или с CORS). НЕ data: URL — на test/prod CSP `connect-src`
 *   их блокирует, qr-code-styling не сможет fetch-нуть.
 * @param {string} [centerText] - Текст для рендера ПОВЕРХ QR в центре
 *   (например "ASK AI"). Реализован как absolute-positioned overlay в JSX
 *   (стили в QRCode.css), а НЕ как image внутри qr-code-styling — иначе
 *   библиотека делает fetch на data: URL и упирается в CSP `connect-src`.
 *   Error-correction level 'H' (30% избыточность) обеспечивает сканируемость
 *   несмотря на overlay-обструкцию в центре.
 * @param {React.ReactNode} [centerLogo] - React node для рендера ПОВЕРХ QR в
 *   центре (например `<BrandArrowIcon />`). Альтернатива `centerText` —
 *   используется тот же overlay-механизм, но без типографики (стили задаёт
 *   модификатор `.qr-code__overlay--logo`). Если переданы оба пропа,
 *   `centerLogo` имеет приоритет.
 * @param {string} [className] - Дополнительный CSS класс для контейнера
 * @param {() => void} [onReady] - Колбэк когда QR полностью отрисован
 *   (qr-code-styling `append()` асинхронный — ждёт загрузку logoUrl и т.д.).
 *   Используется bulk-print страницей чтобы не вызывать window.print() пока
 *   все N QR не готовы.
 * @param {string} [href] - Если задан — QR оборачивается в <a target="_blank">,
 *   чтобы оператор/менеджер в превью печати мог кликнуть и сразу перейти
 *   по закодированной ссылке без сканирования телефоном (issue #942).
 *   Стили `<a>` сбрасываются — визуально QR не меняется, в print-режиме
 *   тоже остаётся прежним (см. QRCode.css → @media print).
 */
const QRCode = ({
  url,
  size = 160,
  fgColor = '#000000',
  bgColor = '#FFFFFF',
  logoUrl,
  centerText,
  centerLogo,
  className,
  onReady,
  href,
  ariaLabel = 'Open landing page encoded in QR',
  // 'rounded' (default) — closed pill modules + extra-rounded corner
  // squares (стандартный «дружелюбный» вид). 'sharp' — square modules
  // и square corners (используется для new-model Autodis, issue #884).
  shape = 'rounded',
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!url) return undefined;

    const isSharp = shape === 'sharp';
    const qrOptions = {
      width: size,
      height: size,
      /* margin: 0 — top-level option qr-code-styling, отключает внешний
         quiet-zone padding canvas-а. По умолчанию lib рендерит ~4-модуль
         буфер белого пространства по периметру (QR-стандарт). Для
         нашего use-case wrapper уже даёт quiet-zone (white background
         + border-radius/overflow), поэтому встроенный margin создавал
         double-spacing — Powered text «отрывался» от визуальных модулей. */
      margin: 0,
      data: url,
      dotsOptions: {
        color: fgColor,
        type: isSharp ? 'square' : 'rounded',
      },
      cornersSquareOptions: {
        type: isSharp ? 'square' : 'extra-rounded',
        color: fgColor,
      },
      cornersDotOptions: {
        type: isSharp ? 'square' : 'dot',
        color: fgColor,
      },
      backgroundOptions: {
        color: bgColor,
      },
      qrOptions: {
        // H — высший уровень коррекции, до 30% модулей могут быть
        // потеряны/закрыты без ущерба для сканирования. Нужен и для
        // logoUrl (image вырезает модули под собой), и для centerText
        // overlay (визуально скрывает модули, scanner всё ещё декодирует).
        errorCorrectionLevel: 'H',
      },
    };

    // logoUrl — внешняя картинка через HTTP(S). qr-code-styling fetch-ит
    // её и вырезает модули под прозрачной частью. data: URL сюда подавать
    // нельзя — CSP `connect-src` на test/prod не разрешает `data:` schema.
    if (logoUrl) {
      qrOptions.image = logoUrl;
      qrOptions.imageOptions = {
        crossOrigin: 'anonymous',
        margin: 0,
        imageSize: 0.3,
        hideBackgroundDots: true,
      };
    }

    const qrCode = new QRCodeStyling(qrOptions);
    let cancelled = false;

    // Захват контейнера в локальную переменную для cleanup —
    // containerRef.current к моменту cleanup может уже быть null/другим
    // элементом (react-hooks/exhaustive-deps правило).
    const container = containerRef.current;
    if (container) {
      // Не очищаем innerHTML — внутри лежит overlay-блок (centerText)
      // как React JSX child. Удаляем ТОЛЬКО предыдущий QR-host если он
      // есть, оставляя overlay нетронутым.
      const previousQr = container.querySelector('[data-qr-canvas]');
      if (previousQr) previousQr.remove();
      const qrHost = document.createElement('div');
      qrHost.dataset.qrCanvas = 'true';
      // qrCode.append() возвращает Promise<void>, который резолвится после
      // того как все внутренние картинки (logoUrl) загружены и canvas
      // отрисован. Дожидаемся этого и сообщаем родителю через onReady —
      // bulk-print страница использует это чтобы не триггерить window.print()
      // пока все N QR не готовы. Порядок (append к detached qrHost → потом
      // attach в DOM) совпадает с main-веткой, чтобы избежать пустого div
      // в DOM до завершения первого render-tick qr-code-styling.
      Promise.resolve(qrCode.append(qrHost))
        .then(() => {
          if (!cancelled) onReady?.();
        })
        .catch((err) => {
          // Не молчим — даём родителю шанс не зависнуть в loading-state.
          console.warn('[QRCode] append failed:', err);
          if (!cancelled) onReady?.();
        });
      container.appendChild(qrHost);
    }

    return () => {
      cancelled = true;
      if (container) {
        const previousQr = container.querySelector('[data-qr-canvas]');
        if (previousQr) previousQr.remove();
      }
    };
  }, [url, size, fgColor, bgColor, logoUrl, onReady, shape]);

  // Динамические prop-значения через CSS custom properties — единственный
  // допустимый случай inline styles per DESIGN.md (данные из props).
  // Статические правила (positioning, font weight, letter spacing) живут
  // в QRCode.css.
  const cssVars = {
    '--qr-size': `${size}px`,
    '--qr-overlay-bg': bgColor,
    '--qr-overlay-fg': fgColor,
  };

  const qrInner = (
    <div ref={containerRef} className={`qr-code ${className || ''}`} style={cssVars}>
      {centerLogo ? (
        <div className="qr-code__overlay qr-code__overlay--logo" aria-hidden="true">
          {centerLogo}
        </div>
      ) : centerText ? (
        <div className="qr-code__overlay" aria-hidden="true">
          {centerText}
        </div>
      ) : null}
    </div>
  );

  // Обёртка <a> — только когда задан href. target=_blank + noopener,noreferrer
  // защищают host page от tab-hijack через window.opener (issue #942).
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="qr-code-link"
        aria-label={ariaLabel}
      >
        {qrInner}
      </a>
    );
  }

  return qrInner;
};

export default QRCode;
